/** Hits and what follows from them (docs/spec/02 §2, §6, §7, §8; 03 §2). */

import { startRescue } from './beam';
import { dropOnKill, losePowers } from './powerups';
import { findEnemy, isAloft, isDiving } from './roster';
import { addScore, chainKill, killPoints } from './score';
import { CHALLENGE, FIELD, FIGHTER, NOVA, SCORE } from './tuning';
import type { Enemy, GameState } from './types';

/** A challenging stage pays for every whole wave (02 §7). */
function countChallengeHit(s: GameState, e: Enemy): void {
  const c = s.challenge;
  if (!c) return;
  c.hits++;
  const n = (c.waveHits[e.wave] ?? 0) + 1;
  c.waveHits[e.wave] = n;
  if (n !== 8) return;
  const bonus = CHALLENGE.waveBonus[Math.min(c.index, CHALLENGE.waveBonus.length) - 1] ?? CHALLENGE.waveBonus[0];
  addScore(s, bonus);
  s.events.push({ type: 'waveBonus', x: e.x, y: e.y, points: bonus });
}

function settlePartner(s: GameState, e: Enemy, diving: boolean): void {
  const other = findEnemy(s, e.partner);
  if (!other) return;
  if (e.kind === 'monarch') {
    // Shot down in flight, its captive is rescued; shot at home, the captive stays, a rogue.
    if (diving) startRescue(s, other);
    else other.partner = -1;
  } else other.partner = -1;
}

function settleTrio(s: GameState, e: Enemy): void {
  if (e.trio === 0) return;
  const left = s.enemies.some((o) => o !== e && !o.gone && o.trio === e.trio);
  if (left || s.director.brokenTrios.includes(e.trio)) return;
  const bonus = SCORE.morphBonus[e.variant] ?? SCORE.morphBonus[0];
  addScore(s, bonus);
  s.events.push({ type: 'morphBonus', x: e.x, y: e.y, points: bonus });
}

export function killEnemy(s: GameState, e: Enemy): void {
  const diving = isDiving(e);
  const aloft = isAloft(e);
  const points = killPoints(s, e) * chainKill(s);
  e.gone = true;
  addScore(s, points);
  const popup = (e.kind === 'monarch' && diving) || e.kind === 'captive';
  s.events.push({ type: 'enemyKilled', x: e.x, y: e.y, kind: e.kind, variant: e.variant, diving, points, popup });
  if (s.phase === 'challenge') countChallengeHit(s, e);
  settlePartner(s, e, diving);
  settleTrio(s, e);
  if (s.mode === 'supernova') dropOnKill(s, e, aloft);
}

/** Hurt an enemy; true if that killed it. */
export function damageEnemy(s: GameState, e: Enemy, damage: number): boolean {
  if (e.gone) return false;
  e.hp -= damage;
  if (e.hp > 0) {
    s.events.push({ type: 'enemyHit', x: e.x, y: e.y, kind: e.kind });
    return false;
  }
  killEnemy(s, e);
  return true;
}

/** The x of each ship the player is flying: one, or two when dual. */
export function shipXs(s: GameState): number[] {
  const p = s.player;
  return p.dual ? [p.x - FIGHTER.dualOffset, p.x + FIGHTER.dualOffset] : [p.x];
}

/** Something has struck ship `index` (0, or 1 for a dual fighter's right-hand ship). */
export function hitShip(s: GameState, index: number): void {
  const p = s.player;
  if (p.safe > 0 || (p.status !== 'flying' && p.status !== 'arriving')) return;
  const x = shipXs(s)[index] ?? p.x;
  if (s.powers.shield) {
    s.powers.shield = false;
    p.safe = NOVA.safeTicks;
    s.events.push({ type: 'shieldBurst', x, y: FIELD.fighterY });
    return;
  }
  if (p.dual) {
    // The other ship flies on from where it is.
    p.dual = false;
    p.x += index === 0 ? FIGHTER.dualOffset : -FIGHTER.dualOffset;
    s.events.push({ type: 'playerDied', x, y: FIELD.fighterY, survivor: true });
    return;
  }
  p.status = 'dead';
  p.timer = 0;
  s.chain = 0;
  losePowers(s);
  s.events.push({ type: 'playerDied', x, y: FIELD.fighterY, survivor: false });
}
